import { StyleSheet } from "react-native";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useFocusEffect, useRouter } from "expo-router";
import { entitySync } from "@/services/sync/entity-sync";

import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { ThemedActionHoverPanel, type IBatchAction } from "@/components/ThemedActionHoverPanel";
import { ShareRecipientSelector } from "@/components/sharing/ShareRecipientSelector";

import { useFirebase } from "@components/firebase/useFirebase";
import { getMyMemories, deleteMemory, editMemory } from "@/services/firebase/memory-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { getMyMedia } from "@/services/firebase/media-functions";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { getMyContacts } from "@/services/firebase/user-functions";
import { getMyGroups } from "@/services/firebase/group-functions";
import { MemoryModel, MediaModel, ShareModel, ShareSummary, Contact, GroupModel, formatLocation, formatDisplayDate, DateParser } from "common247";
import { MemoryCard } from "@/components/memory/MemoryCard";
import { CollapsibleFilterSection } from "@/components/filter";
import { useFilterPersistence } from "@/hooks/useFilterPersistence";
import { filterMemories, computeFilteredCounts } from "@/services/unified-filter-service";
import { sendMemoryTo } from "@/services/memory-pdf-exporter";
import { downloadMediaFile } from "@/utils/media-download";
import { formatFavouriteRatingGroup } from "@/components/media/MediaGallery";
import type { ICustomFilterOption } from "@/components/ThemedList";

/**
 * Extracts the 4-digit calendar year from a memory's date (or JSON block) for grouping.
 */
export function getMemoryDateYear(m: MemoryModel): string {
  if (m.date) {
    const parsed = DateParser.parse(m.date);
    if (parsed.from?.year) {
      return String(parsed.from.year);
    }
  }
  if (m.createdAt) {
    const d = new Date(m.createdAt);
    if (!isNaN(d.getTime())) {
      return String(d.getFullYear());
    }
  }
  return "No Date";
}

export const MEMORY_SORT_OPTIONS: ISortOption<MemoryModel>[] = [
  { key: "date", label: "Date", getValue: (m) => formatDisplayDate(m.date) },
  { key: "favouriteRating", label: "Favourite Rating", getValue: (m) => m.favouriteRating ?? 3 },
  { key: "title", label: "Title", getValue: (m) => m.title },
  { key: "location", label: "Location", getValue: (m) => formatLocation(m.location) },
  { key: "owner", label: "Owner", getValue: (m) => m.ownerDisplayName || "" },
];

const MEMORY_FAVOURITE_GROUP_OPTION: IGroupOption<MemoryModel> = {
  key: "favouriteRating",
  label: "Favourite Rating",
  getGroupValue: (m) => formatFavouriteRatingGroup(m.favouriteRating),
};

export const MEMORY_GROUP_OPTIONS: IGroupOption<MemoryModel>[] = [
  MEMORY_FAVOURITE_GROUP_OPTION,
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (m) => m.ownerDisplayName || "Unknown Owner",
  },
  {
    key: "year",
    label: "Year",
    getGroupValue: (m) => getMemoryDateYear(m),
  },
  {
    key: "role",
    label: "Role",
    getGroupValue: (m) => (m.userRole === "owner" ? "My Memories (Owner)" : `Shared with me (${m.userRole || "Viewer"})`),
  },
  {
    key: "tag",
    label: "Primary Tag",
    getGroupValue: (m) => {
      const tags = Object.values(m.tags || {});
      return tags.length > 0 ? tags[0].value : "Untagged";
    },
  },
];

export const MEMORY_FAVOURITE_FILTER: ICustomFilterOption<MemoryModel> = {
  key: "favouriteRating",
  label: "Rating:",
  defaultValue: "all",
  options: [
    { label: "All Ratings", value: "all" },
    { label: "★ 5 Stars", value: "5" },
    { label: "★ 4 Stars", value: "4" },
    { label: "★ 3 Stars", value: "3" },
    { label: "★ 2 Stars", value: "2" },
    { label: "★ 1 Star", value: "1" },
    { label: "4+ Stars", value: "4_plus" },
    { label: "3+ Stars", value: "3_plus" },
  ],
  predicate: (item, val) => {
    const rate = item.favouriteRating ?? 3;
    if (val === "all") return true;
    if (val === "4_plus") return rate >= 4;
    if (val === "3_plus") return rate >= 3;
    return rate === Number(val);
  },
};

export default function MyMemoriesScreen() {
  const services = useFirebase();
  const router = useRouter();
  const [memories, setMemories] = useState<MemoryModel[]>([]);
  const [media, setMedia] = useState<MediaModel[]>([]);
  const [loading, setLoading] = useState(true);

  // Unified Filter Persistence
  const { filter, setFilter, resetFilter } = useFilterPersistence("memories");

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Sharing state
  const [sharingMemory, setSharingMemory] = useState<MemoryModel | null>(null);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [sharesByEntityId, setSharesByEntityId] = useState<Record<string, ShareModel[]>>({});
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  const effectiveMemories = useMemo(() => {
    return filterMemories(memories, filter);
  }, [memories, filter]);

  const filteredCounts = useMemo(() => {
    return computeFilteredCounts({ memories }, filter, "memories");
  }, [memories, filter]);

  const isSelectionActive = selectionMode || selectedIds.length > 0;

  const fetchMemories = useCallback(async () => {
    if (!services) return;
    setLoading(true);
    try {
      const [memsRes, mediaRes, contactsRes, groupsRes] = await Promise.all([
        getMyMemories(services, { filter }),
        getMyMedia(services, { filter }).catch(() => ({ status: false, media: [] })),
        getMyContacts(services, { includeInactive: false }).catch(() => ({ status: false, contacts: [] })),
        getMyGroups(services, { filter }).catch(() => ({ status: false, groups: [] })),
      ]);

      if (memsRes.status) {
        setMemories(memsRes.memories || []);
      } else {
        CustomAlert.alert("Error", memsRes.error || "Failed to load memories.");
      }
      if (mediaRes.status && mediaRes.media) {
        setMedia(mediaRes.media);
      }
      if (contactsRes.status && contactsRes.contacts) {
        setContacts(contactsRes.contacts);
      }
      if (groupsRes.status && groupsRes.groups) {
        setGroups(groupsRes.groups);
      }
    } catch (error) {
      console.error("Error fetching memories:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching memories.");
    } finally {
      setLoading(false);
    }
  }, [services, filter]);

  const fetchSharingData = async (memoryId: string) => {
    if (!services) return;
    setLoadingShares(true);
    try {
      const [sharesRes, contactsRes, groupsRes] = await Promise.all([
        getSharesForSource(services, { sourceId: memoryId, sourceType: "memory" }),
        getMyContacts(services, { includeInactive: false }),
        getMyGroups(services, {}),
      ]);
      if (sharesRes.status) {
        setShares(sharesRes.shares);
        setSharesByEntityId((prev) => ({ ...prev, [memoryId]: sharesRes.shares }));
        const updatedSummaries: ShareSummary[] = sharesRes.shares.map((s) => ({
          id: s.targetId,
          displayName: s.targetDisplayName || "Unknown",
          type: s.targetType,
          role: s.role,
          targetUserId: s.targetUserId,
        }));
        setMemories((prev) =>
          prev.map((m) => (m.id === memoryId ? { ...m, sharedWith: updatedSummaries } : m))
        );
      }
      if (contactsRes.status) setContacts(contactsRes.contacts);
      if (groupsRes.status) setGroups(groupsRes.groups);
    } catch (error) {
      console.error("Error fetching sharing data:", error);
    } finally {
      setLoadingShares(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchMemories();
    }, [fetchMemories])
  );

  useEffect(() => {
    const unsubscribe = entitySync.subscribe((event) => {
      if (event.type === "memory") {
        if (event.action === "update" && event.data) {
          setMemories((prev) =>
            prev.map((m) => (m.id === event.id ? { ...m, ...event.data } : m))
          );
        } else if (event.action === "delete") {
          setMemories((prev) => prev.filter((m) => m.id !== event.id));
        } else {
          fetchMemories();
        }
      }
    });
    return unsubscribe;
  }, [fetchMemories]);

  const toggleItemSelection = (memory: MemoryModel) => {
    if (!memory.id) return;
    const isSelected = selectedIds.includes(memory.id);
    const newSelected = isSelected
      ? selectedIds.filter((id) => id !== memory.id)
      : [...selectedIds, memory.id];
    setSelectedIds(newSelected);
    if (newSelected.length === 0) {
      setSelectionMode(false);
    }
  };

  const handleItemPress = (memory: MemoryModel) => {
    if (isSelectionActive) {
      toggleItemSelection(memory);
    } else {
      router.push(`/my-memories/${memory.id}`);
    }
  };

  const handleItemLongPress = (memory: MemoryModel) => {
    if (!memory.id) return;
    setSelectionMode(true);
    if (!selectedIds.includes(memory.id)) {
      setSelectedIds((prev) => [...prev, memory.id!]);
    }
  };

  const handleItemContextMenu = (memory: MemoryModel, _event?: any) => {
    handleItemLongPress(memory);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectionMode(false);
  };

  const handleSelectAll = () => {
    setSelectedIds(effectiveMemories.map((m) => m.id!).filter(Boolean));
  };

  const handleBatchDownload = () => {
    const selectedMemories = memories.filter((m) => selectedIds.includes(m.id!));
    let downloadedCount = 0;
    selectedMemories.forEach((mem) => {
      const mediaList = mem.mediaItems || [];
      if (mediaList.length > 0) {
        mediaList.forEach((mediaItem, idx) => {
          if (mediaItem.downloadUrl) {
            downloadMediaFile(mediaItem.downloadUrl, `${mem.title}_${idx + 1}.jpg`);
            downloadedCount++;
          }
        });
      }
    });
    if (downloadedCount === 0) {
      CustomAlert.alert("Download", "No media files attached to the selected memories.");
    }
  };

  const handleBatchSendTo = async () => {
    const selectedMemories = memories.filter((m) => selectedIds.includes(m.id!));
    if (selectedMemories.length === 0) return;
    for (const mem of selectedMemories) {
      try {
        await sendMemoryTo(mem);
      } catch (err) {
        console.error("Error sending memory:", err);
      }
    }
  };

  const handleBatchDelete = () => {
    if (!services || selectedIds.length === 0) return;
    CustomAlert.alert(
      "Delete Memories",
      `Are you sure you want to delete ${selectedIds.length} selected memory record(s)? This action cannot be undone.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            let deletedCount = 0;
            for (const id of selectedIds) {
              try {
                const res = await deleteMemory(services, { memoryId: id });
                if (res.status) deletedCount++;
              } catch (err) {
                console.error("Error deleting memory:", err);
              }
            }
            CustomAlert.alert("Deleted", `Deleted ${deletedCount} memory record(s).`);
            handleClearSelection();
            fetchMemories();
          },
        },
      ]
    );
  };

  const handleBatchShareConfirm = async (
    targetId: string,
    targetType: "contact" | "group",
    role: "viewer" | "editor"
  ) => {
    if (!services) return;
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        const res = await share(services, {
          sourceId: id,
          sourceType: "memory",
          targetId,
          targetType,
          role,
        });
        if (res.status) successCount++;
      } catch (err) {
        console.error("Error sharing memory:", err);
      }
    }
    CustomAlert.alert(
      "Sharing Complete",
      `Shared ${successCount} memory record(s) with ${targetType}.`
    );
    handleClearSelection();
    fetchMemories();
  };


  const batchActions: IBatchAction[] = [
    {
      id: "download",
      label: "Download",
      icon: "download-outline",
      onPress: handleBatchDownload,
    },
    {
      id: "share",
      label: "Share",
      icon: "share-social-outline",
      onPress: () => setIsShareModalOpen(true),
    },
    {
      id: "sendTo",
      label: "Send To",
      icon: "paper-plane-outline",
      onPress: handleBatchSendTo,
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash-outline",
      destructive: true,
      onPress: handleBatchDelete,
      variant: "destructive",
    },
  ];

  const handleShare = async (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => {
    if (!services || !sharingMemory) return;
    try {
      const res = await share(services, {
        sourceId: sharingMemory.id!,
        sourceType: "memory",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        fetchSharingData(sharingMemory.id!);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to share.");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services || !sharingMemory) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        fetchSharingData(sharingMemory.id!);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to remove share.");
      }
    } catch (error) {
      console.error("Error unsharing:", error);
    }
  };

  const handleDeleteMemory = async (memoryId: string) => {
    if (!services) return;
    const targetMemory = memories.find((m) => m.id === memoryId);
    await confirmEntityDeletion({
      services,
      entityType: "memory",
      entityId: memoryId,
      entityName: targetMemory?.title,
      onProceedDelete: (cascadeUnlink) => deleteMemory(services, { memoryId, cascadeUnlink }),
      onSuccess: () => fetchMemories(),
    });
  };

  const handleToggleInlineSharing = (item: MemoryModel) => {
    if (sharingMemory?.id === item.id) {
      setSharingMemory(null);
    } else {
      setSharingMemory(item);
      fetchSharingData(item.id!);
    }
  };

  return (
    <ThemedScreen
      title="Memories"
      loading={loading}
      scrollable={false}
      rightIcon={{
        name: "git-network-outline",
        onPress: () => router.push("/my-memories/visualiser"),
      }}
    >
      <ThemedView style={styles.container}>
        {/* Unified Collapsible Entity Filter */}
        <CollapsibleFilterSection
          screen="memories"
          filter={filter}
          onFilterChange={setFilter}
          counts={filteredCounts}
          onReset={resetFilter}
          allMemories={memories}
          allMedia={media}
          allContacts={contacts}
          allGroups={groups}
        />

        <ThemedList<MemoryModel>
          data={effectiveMemories}
          sortOptions={MEMORY_SORT_OPTIONS}
          defaultSortKey="date"
          defaultSortDirection="desc"
          groupOptions={MEMORY_GROUP_OPTIONS}
          customFilters={[MEMORY_FAVOURITE_FILTER]}
          persistenceKey="my-memories"
          filterKeys={["title", "description", "location"]}
          filterPlaceholder="Search memories..."
          loading={loading}
          onRefresh={fetchMemories}
          refreshing={loading}
          addButton={{
            title: isSelectionActive ? "Back" : "+ Add Memory",
            onPress: () => (isSelectionActive ? handleClearSelection() : router.push("/my-memories/new")),
            variant: isSelectionActive ? "secondary" : "primary",
          }}
          onItemPress={handleItemPress}
          onItemLongPress={handleItemLongPress}
          onItemContextMenu={handleItemContextMenu}
          emptyText="No memories found."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, isHovered, viewMode) => {
            const isExpanded = sharingMemory?.id === item.id;
            const isSelected = selectedIds.includes(item.id!);

            return (
              <MemoryCard
                memory={item}
                showOwner={item.userRole !== "owner"}
                currentUserId={services?.auth?.currentUser?.uid}
                viewMode={viewMode}
                isHovered={isHovered}
                selectionMode={isSelectionActive}
                isSelected={isSelected}
                onPress={() => handleItemPress(item)}
                onLongPress={() => handleItemLongPress(item)}
                onContextMenu={(e) => handleItemContextMenu(item, e)}
                onEditPress={() => router.push(`/my-memories/${item.id}`)}
                onDelete={() => handleDeleteMemory(item.id!)}
                isSharingExpanded={isExpanded}
                onToggleSharing={() => handleToggleInlineSharing(item)}
                shares={sharesByEntityId[item.id!] ?? (isExpanded ? shares : undefined)}
                availableContacts={contacts}
                availableGroups={groups}
                isLoadingShares={isExpanded && loadingShares}
                onShareTarget={handleShare}
                onUnshare={handleUnshare}
                onSendTo={sendMemoryTo}
              />
            );
          }}
        />

        {/* Floating Contextual Batch Action Panel */}
        <ThemedActionHoverPanel
          visible={selectedIds.length > 0}
          selectedCount={selectedIds.length}
          totalCount={effectiveMemories.length}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          actions={batchActions}
        />

        {/* Batch Share Modal */}
        <ShareRecipientSelector
          visible={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          selectedCount={selectedIds.length}
          itemTypeLabel="memories"
          onConfirmShare={handleBatchShareConfirm}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    paddingBottom: 0,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 40,
    gap: 6,
  },
});
